import type { IyzicoOrderRequest } from '../types/payment';

const KEY_PREFIX = 'hsl_iyzico_pending_';
const DONE_PREFIX = 'hsl_iyzico_done_';
const ORDER_PREFIX = 'hsl_iyzico_order_';

/** Strict Mode remount'larında aynı token için tek complete isteği */
const inflightComplete = new Map<string, Promise<unknown>>();

export type PendingIyzicoCheckout = {
  conversationId: string;
  order: IyzicoOrderRequest;
};

function tokenKey(token: string) {
  return `${KEY_PREFIX}${token}`;
}

function conversationKey(conversationId: string) {
  return `${KEY_PREFIX}conv_${conversationId}`;
}

function doneKey(token: string) {
  return `${DONE_PREFIX}${token}`;
}

export function savePendingIyzicoCheckout(token: string, pending: PendingIyzicoCheckout): void {
  const data = JSON.stringify(pending);
  sessionStorage.setItem(tokenKey(token), data);
  sessionStorage.setItem(conversationKey(pending.conversationId), data);
}

export function loadPendingIyzicoCheckout(
  token: string,
  conversationId?: string | null,
): PendingIyzicoCheckout | null {
  const candidates = [tokenKey(token)];
  if (conversationId) {
    candidates.push(conversationKey(conversationId));
  }

  for (const key of candidates) {
    const raw = sessionStorage.getItem(key);
    if (!raw) continue;

    try {
      return JSON.parse(raw) as PendingIyzicoCheckout;
    } catch {
      // try next key
    }
  }

  return null;
}

export function clearPendingIyzicoCheckout(token: string, conversationId?: string | null): void {
  sessionStorage.removeItem(tokenKey(token));
  if (conversationId) {
    sessionStorage.removeItem(conversationKey(conversationId));
  }
}

export function markIyzicoCheckoutCompleted(token: string): void {
  sessionStorage.setItem(doneKey(token), '1');
}

export function isIyzicoCheckoutCompleted(token: string): boolean {
  return sessionStorage.getItem(doneKey(token)) === '1';
}

export type CompletedOrderRef = {
  id: string;
  orderNumber?: number;
};

export function saveIyzicoCompletedOrder(token: string, order: CompletedOrderRef): void {
  sessionStorage.setItem(`${ORDER_PREFIX}${token}`, JSON.stringify(order));
}

/** @deprecated use saveIyzicoCompletedOrder */
export function saveIyzicoCompletedOrderId(token: string, orderId: string): void {
  saveIyzicoCompletedOrder(token, { id: orderId });
}

export function loadIyzicoCompletedOrder(token: string): CompletedOrderRef | null {
  const raw = sessionStorage.getItem(`${ORDER_PREFIX}${token}`);
  if (!raw) return null;

  if (!raw.startsWith('{')) {
    return { id: raw };
  }

  try {
    return JSON.parse(raw) as CompletedOrderRef;
  } catch {
    return { id: raw };
  }
}

/** @deprecated use loadIyzicoCompletedOrder */
export function loadIyzicoCompletedOrderId(token: string): string | null {
  return loadIyzicoCompletedOrder(token)?.id ?? null;
}

/** Aynı ödeme token'ı için eşzamanlı complete çağrılarını tek isteğe indirger. */
export function runIyzicoCompleteOnce<T>(token: string, run: () => Promise<T>): Promise<T> {
  if (isIyzicoCheckoutCompleted(token)) {
    return Promise.reject(new Error('IYZICO_ALREADY_COMPLETED'));
  }

  const existing = inflightComplete.get(token);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = run()
    .then((result) => {
      markIyzicoCheckoutCompleted(token);
      return result;
    })
    .finally(() => {
      inflightComplete.delete(token);
    });

  inflightComplete.set(token, promise);
  return promise;
}
